import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RedeemIcon from '@mui/icons-material/Redeem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CircularProgress } from '@mui/material';

import { useHistory } from 'react-router-dom';
import { usePromotion,
  Promotion,
  PromotionStatus,
  Reward as RewardProps,
  UserData,
  PromotionData } from './PromotionContext';
import { ActivatePromotion } from '../../activation/Activation.model';

/* For testing purpose */
import rewardImgPlaceholder from '../../_assets/bhvr-footer.png';

function ClaimButton({ onClaim, status, claimInProgress, requiredGameId }:
{ onClaim: () => void, status: PromotionStatus, claimInProgress: boolean, requiredGameId: string }): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();

  const activateReward = (): void => {
    history.push(`/account/my-account/games/${requiredGameId}/activation?provider=steam`);
  };

  return (
    <div className='reward-button'>
      {!status && !location.pathname.includes('activation') && (
        <button
          type='button'
          data-cy='activation-button'
          className={`btn btn-primary-game ${claimInProgress ? 'inProgress' : ''}`}
          onClick={activateReward}
        >
          {!claimInProgress
            ? (
              <>
                {t('activation.stepActivation')}
              </>
            )
            : <div className='loader'><CircularProgress /></div>}
        </button>
      )}

      {status === PromotionStatus.Claimable && (

        <button
          type='button'
          data-cy='reward-claim-button'
          className={`btn btn-primary-game ${claimInProgress ? 'inProgress' : ''}`}
          onClick={onClaim}
        >
          {!claimInProgress
            ? (
              <>
                <RedeemIcon />
                {t('button.claim-now')}
              </>
            )

            : <div className='loader'><CircularProgress /></div>}

        </button>

      )}

      {status === PromotionStatus.Claimed && (
        <button type='button' data-cy='reward-claimed-button' className='btn btn-primary-game claimed' disabled>
          <CheckCircleIcon />
          {t('button.claimed')}
        </button>
      )}
    </div>
  );
}

function ClaimedCode({ userData }: { userData: { promocode: { code: string } } }): JSX.Element {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);

  const copy = (): void => {
    navigator.clipboard.writeText(userData?.promocode.code);
    setIsCopied(true);
  };

  return (
    <div className='reward-claimed'>
      <input
        readOnly
        className='promoCode-input form-control bg-dark text-white input-lg'
        type='text'
        value={userData.promocode.code}
        id='promoCode-input'
        data-cy='reward-code-text'
      />

      {isCopied
            && (
              <button
                className='copy-btn btn btn-primary-game copied'
                type='button'
                data-cy='reward-copy'
                onClick={copy}
              >
                <ContentCopyIcon />
                {t('button.copied')}
              </button>
            )}

      {!isCopied
            && (
              <button
                className='copy-btn btn btn-primary-game'
                type='button'
                data-cy='reward-copy'
                onClick={copy}
              >
                <ContentCopyIcon />
                {t('button.copy')}
              </button>
            )}

    </div>
  );
}

function Reward(props: RewardProps): JSX.Element {
  const { displayName, gameSpecificData } = props;
  const { t } = useTranslation(['promotions']);

  return (
    <div className='container reward'>
      {gameSpecificData.rarity && (
        <section className='row rarity-wrap'>
          <div className='col rarity-container'>
            <span className='rarity-content'>{t(`promotions:${gameSpecificData.rarity}`)}</span>
          </div>
        </section>
      )}
      <section className='row img-wrap'>
        <div className='col img-container'>
          <img className='img-content' src={props.pictureURL || rewardImgPlaceholder} alt='' />
        </div>
      </section>
      <section className='row description-wrap'>
        <div className='col description-container'>
          {gameSpecificData.universalStatus && (
            <span className='description-universal-status'>
              {t(`promotions:${gameSpecificData.universalStatus}`)}
            </span>
          )}
          <h3 className='description-display-name'>{t(`promotions:${displayName}`)}</h3>
        </div>
      </section>
    </div>
  );
}

interface Props {
  promotion?: Promotion;
  activatePromotion?: ActivatePromotion;
}

export function PromotionCard(props: Props): JSX.Element {
  const { claim } = usePromotion();

  const [userData, setUserData] = useState<UserData>(null);
  const [promotionData, setPromotionData] = useState<PromotionData>(null);
  const [status, setStatus] = useState(null);
  const [claimInProgress, setClaimInProgress] = useState(false);

  const onClaim = async (): Promise<void> => {
    setClaimInProgress(true);
    try {
      await claim(promotionData.promotionId);
    } finally {
      setClaimInProgress(false);
    }
  };

  useEffect(() => {
    if (props.promotion) {
      setPromotionData(props.promotion.promotionData);
      if (props.promotion.userData) {
        setUserData(props.promotion.userData);
        setStatus(props.promotion.userData.status);
      }
    }
  }, [props.promotion]);

  useEffect(() => {
    if (props.activatePromotion) {
      setPromotionData(props.activatePromotion);
    }
  }, [props.activatePromotion]);

  return (
    <div className='promotion-container'>
      <div className='rewards-container'>
        {promotionData?.rewards?.map((reward) => (
          <Reward key={reward.displayName} {...reward} />
        ))}
      </div>

      {status === PromotionStatus.Claimed && <ClaimedCode userData={userData} />}

      <ClaimButton
        status={status}
        onClaim={onClaim}
        claimInProgress={claimInProgress}
        requiredGameId={promotionData?.requiredGameIds[0]}
      />
    </div>
  );
}
